// Cartoon character in the real world's cast stage (spec G14-cartoon-in-the-real-world, PR d; no
// live equivalent, Cartoon-1): the draft's sixteen stills, the three sheets wide and the plate,
// the room and the eleven shots 9:16, each with its Regenerate, and Start over. As Flat cartoon's
// (`FlatCartoonCast.tsx`).
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { CartoonDraftStill, CartoonStillName } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { STILL_ORDER, stillWaiting } from "./cartoon-real-world-draft";
import classes from "./podcast.module.css";

/** Each still's label: the playbook's names (L1584-1600). */
export const STILL_LABELS: Readonly<Record<CartoonStillName, string>> = {
  creature_sheet: "Creature",
  scale_plate: "Scale plate",
  woman_sheet: "Woman",
  room_master: "Room",
  product_sheet: "Product",
  s1: "Shot 1",
  s2: "Shot 2",
  s3: "Shot 3",
  s4: "Shot 4",
  s5: "Shot 5",
  s6: "Shot 6",
  s7: "Shot 7",
  s8: "Shot 8",
  s9: "Shot 9",
  s10: "Shot 10",
  s11: "Shot 11",
};
const PENDING = { status: "pending", url: null, problem: null } as const;

function stillItem(still: CartoonDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface CartoonRealWorldCastProps {
  stills: readonly CartoonDraftStill[];
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: CartoonStillName) => void;
  onStartOver: () => void;
}

/** "Cast & set" and the sixteen stills, each with its label; a done still opens in the lightbox. */
export function CartoonRealWorldCast({
  stills,
  busy,
  resetting,
  onRegenerate,
  onStartOver,
}: CartoonRealWorldCastProps) {
  const [preview, setPreview] = useState<CartoonStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const made = stills.flatMap((still) => (still.url ? [{ ...still, url: still.url }] : []));
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button variant="subtle" size="compact-sm" disabled={resetting || busy} onClick={() => setConfirming(true)}>
          Start over
        </Button>
      </Group>
      <div className={classes.stills}>
        {STILL_ORDER.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={stillWaiting(name, stills)}
            busy={busy}
            wide={name.endsWith("_sheet")}
            onRegenerate={() => onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
          />
        ))}
      </div>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as CartoonStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script and the sixteen images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
